import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "@tablemint/i18n";
import AnimatedSection from "../components/animations/AnimatedSection";

const About: React.FC = () => {
  // Common translations
  const { t } = useTranslation("common");

  // About page translations - Using explicit namespace
  const { t: tAbout } = useTranslation("about" as any);

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative pt-32 py-20 px-4 md:px-6 lg:px-8 overflow-hidden">
        <div className="absolute inset-0 opacity-10 bg-gradient-to-r from-primary/30 to-primary/10 z-0" />
        <div className="container mx-auto max-w-6xl relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6">
              {tAbout("hero.title")}
            </h1>
            <p className="text-xl md:text-2xl text-foreground/80 max-w-3xl mx-auto mb-8">
              {tAbout("hero.subtitle")}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Our Story Section */}
      <AnimatedSection
        className="py-16 md:py-24 px-4 md:px-6 lg:px-8 bg-background"
        threshold={0.1}
      >
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
                {tAbout("story.title")}
              </h2>
              <p className="text-foreground/80 mb-6">
                {tAbout("story.paragraph1")}
              </p>
              <p className="text-foreground/80">{tAbout("story.paragraph2")}</p>
            </div>
            <div className="relative h-80 md:h-96 rounded-lg overflow-hidden shadow-xl">
              <img
                src="/assets/images/restaurant-owner.jpg"
                alt={tAbout("story.imageAlt")}
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* Mission and Values */}
      <AnimatedSection
        className="py-16 md:py-24 px-4 md:px-6 lg:px-8 bg-muted/30"
        threshold={0.1}
      >
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-12 text-center">
            {tAbout("mission.title")}
          </h2>
          <div className="grid md:grid-cols-2 gap-12">
            <div className="bg-background p-8 rounded-lg shadow-md">
              <h3 className="text-2xl font-bold text-foreground mb-4">
                {tAbout("mission.missionTitle")}
              </h3>
              <p className="text-foreground/80">
                {tAbout("mission.missionText")}
              </p>
            </div>
            <div className="bg-background p-8 rounded-lg shadow-md">
              <h3 className="text-2xl font-bold text-foreground mb-4">
                {tAbout("mission.visionTitle")}
              </h3>
              <p className="text-foreground/80">
                {tAbout("mission.visionText")}
              </p>
            </div>
          </div>

          <h3 className="text-2xl font-bold text-foreground mt-16 mb-8 text-center">
            {tAbout("values.title")}
          </h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3, 4, 5, 6].map((index) => (
              <div
                key={index}
                className="bg-background p-6 rounded-lg shadow-sm border border-border"
              >
                <h4 className="text-xl font-semibold text-foreground mb-3">
                  {tAbout(`values.value${index}.title`)}
                </h4>
                <p className="text-foreground/80 text-sm">
                  {tAbout(`values.value${index}.description`)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </AnimatedSection>

      {/* Team Section - Single Developer */}
      <AnimatedSection
        className="py-16 md:py-24 px-4 md:px-6 lg:px-8 bg-background"
        threshold={0.1}
      >
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-12 text-center">
            {tAbout("team.title")}
          </h2>
          <p className="text-foreground/80 text-center max-w-3xl mx-auto mb-12">
            {tAbout("team.description")}
          </p>

          <div className="flex justify-center">
            <div className="text-center max-w-md">
              <div className="relative w-48 h-48 mx-auto mb-6 overflow-hidden rounded-full bg-muted/20">
                <img
                  src="/assets/images/team-member.jpg"
                  alt={tAbout("team.founder.name")}
                  className="w-full h-full object-cover"
                />
              </div>
              <h3 className="text-2xl font-bold text-foreground">
                {tAbout("team.founder.name")}
              </h3>
              <p className="text-primary font-medium mb-3">
                {tAbout("team.founder.position")}
              </p>
              <p className="text-foreground/70">{tAbout("team.founder.bio")}</p>
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* Contact CTA */}
      <AnimatedSection
        className="py-16 md:py-24 px-4 md:px-6 lg:px-8 bg-primary/10"
        threshold={0.1}
      >
        <div className="container mx-auto max-w-3xl text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
            {tAbout("contact.title")}
          </h2>
          <p className="text-xl text-foreground/80 mb-8">
            {tAbout("contact.description")}
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="mailto:contact@tablemint.com"
              className="px-6 py-3 bg-primary hover:bg-primary/90 text-white rounded-lg font-medium shadow-sm transition-colors"
            >
              {tAbout("contact.emailButton")}
            </a>
            <a
              href="/contact"
              className="px-6 py-3 bg-background hover:bg-muted text-foreground rounded-lg font-medium shadow-sm border border-border transition-colors"
            >
              {tAbout("contact.contactButton")}
            </a>
          </div>
        </div>
      </AnimatedSection>
    </div>
  );
};

export default About;
